/**
 * Service for generating short-lived browser-extension pairing codes via the backend API.
 */

import { getApiEndpoints } from "../config/AuthConfigContext";
import { getStoredTokenData } from "./memberManagementService";

/**
 * Request payload for generating a browser-extension pairing code.
 */
export interface CreatePairingCodePayload {
  /** Stable extension install id copied from the browser extension popup. */
  device_id: string;
}

/**
 * Result of a pairing-code generation request.
 */
export interface CreatePairingCodeResponse {
  /** Whether the pairing code was generated successfully. */
  success: boolean;
  /** Human-typeable pairing code in `xxxx-xxxx` format (present on success). */
  code?: string;
  /** Seconds until the generated code expires (present on success). */
  expiresIn?: number;
  /** Human-readable failure reason (present on error). */
  message?: string;
}

/**
 * Generates a short-lived pairing code that links the current dashboard user to
 * a browser extension install.
 *
 * Sends the authenticated RBAC bearer token; the backend derives the user
 * identity from the token and binds the supplied `device_id` to the code.
 *
 * @param payload - Extension install id to bind into the pairing record
 * @returns A result object with the generated code and TTL, or a failure message
 */
export const createPairingCode = async (
  payload: CreatePairingCodePayload,
): Promise<CreatePairingCodeResponse> => {
  try {
    const token = getStoredTokenData()?.token;

    if (!token) {
      throw new Error("Bearer token not available");
    }

    const endpoints = getApiEndpoints();

    const response = await fetch(endpoints.PAIRING_CODE, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      return {
        success: false,
        message: errorData?.detail || response.statusText,
      };
    }

    const responseData = await response.json();

    return {
      success: true,
      code: responseData.code,
      expiresIn: responseData.expires_in,
    };
  } catch (error) {
    return {
      success: false,
      message:
        error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
};
